import React, { useState, useEffect, useRef } from 'react';
import './Match.scss';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearMatch,
  getAwayCaptains,
  getAwayCoaches,
  getAwayTeamRoster,
  getHomeCaptains,
  getHomeCoaches,
  getHomeTeamRoster,
  getMatchDetails,
  getMatchPhotos,
} from '../store/actions/match';
import { getMyPlayer } from '../store/actions/user';
import GameResult from '../components/Match/GameResult';
import MatchDetailBox from '../components/Match/MatchDetailBox';
import YoutubeEmbed from '../components/Common/YoutubeEmbed';
import TeamRoster from '../components/Team/TeamRoster';
import MatchRoster from '../components/Match/MatchRoster';
import MatchSignUp from '../components/Match/MatchSignUp';
import MatchPhotos from '../components/Match/MatchPhotos';
import TabComponent from '../components/Common/TabComponent';
import MatchOverviewTab from '../components/Match/MatchOverviewTab';
import PlayByPlayTab from '../components/Match/PlayByPlayTab';
import ShootingChartTab from '../components/Match/ShootingChartTab';
import GameStatsTab from '../components/Match/GameStatsTab';
import PhotosTab from '../components/Match/PhotosTab';
import HeadToHeadTab from '../components/Match/HeadToHeadTab';
import { TodayISO, MatchLimitISO } from '../const/DatetimeISO';

const Match = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { matchDetails, homeTeamRoster, awayTeamRoster, matchPhotos } = useSelector(
    (store) => store.matchReducer,
  );
  const { userToken } = useSelector((store) => store.loginReducer);
  const { playerInfo } = useSelector((store) => store.userReducer);
  const [currentPlayer, setCurrenetPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLimit, setTimeLimit] = useState('');
  const [isStarted, setIsStarted] = useState(true);
  const hasFetchedTeamData = useRef(false);
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  const today = TodayISO();

  useEffect(() => {
    if (today <= timeLimit) {
      setIsStarted(false);
    }
  }, [timeLimit]);

  useEffect(() => {
    if (userToken) {
      dispatch(getMyPlayer(userToken.id));
    }
  }, [userToken]);

  useEffect(() => {
    dispatch(getMatchDetails(id));
    return () => {
      dispatch(clearMatch());
    };
  }, [id, dispatch]);

  // Poll match details every 5 seconds when match is ongoing
  useEffect(() => {
    // Poll when match has started (scores are numbers, not '-') and match hasn't finished yet
    // Match is finished when either team has won (home_win or away_win is true)
    const isMatchOngoing =
      matchDetails &&
      matchDetails.home_score !== '-' &&
      matchDetails.away_score !== '-' &&
      !matchDetails.home_win &&
      !matchDetails.away_win;

    // Only start polling if match is ongoing and we're not already polling
    if (isMatchOngoing && !isPollingRef.current) {
      isPollingRef.current = true;
      pollingIntervalRef.current = setInterval(() => {
        dispatch(getMatchDetails(id));
      }, 5000); // Poll every 5 seconds
    }
    // Stop polling if match is no longer ongoing but we were polling
    else if (!isMatchOngoing && isPollingRef.current) {
      isPollingRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [matchDetails, id, dispatch]);

  // Fetch team data (roster, captains, coaches, photos) only once when matchDetails is first loaded
  useEffect(() => {
    if (matchDetails && !hasFetchedTeamData.current) {
      dispatch(getHomeTeamRoster(matchDetails.home_team.id, matchDetails.season_id));
      dispatch(getAwayTeamRoster(matchDetails.away_team.id, matchDetails.season_id));
      dispatch(getHomeCaptains(matchDetails.home_team.id));
      dispatch(getAwayCaptains(matchDetails.away_team.id));
      dispatch(getHomeCoaches(matchDetails.home_team.id));
      dispatch(getAwayCoaches(matchDetails.away_team.id));
      dispatch(getMatchPhotos(id));
      hasFetchedTeamData.current = true;
    }
  }, [matchDetails, id, dispatch]);

  // Reset the ref when match ID changes (navigating to a different match)
  useEffect(() => {
    hasFetchedTeamData.current = false;
  }, [id]);

  useEffect(() => {
    if (!currentPlayer && !isStarted) {
      homeTeamRoster?.map((player) => {
        if (player.player_id === playerInfo?.id) {
          setCurrenetPlayer(player);
          return;
        }
      });

      awayTeamRoster?.map((player) => {
        if (player.player_id === playerInfo?.id) {
          setCurrenetPlayer(player);
          return;
        }
      });

      let terminator = false;
      matchDetails?.home_team.match_stats.map((player) => {
        if (player.player_id === playerInfo?.id) {
          setCurrenetPlayer(player);
          setIsPlaying(true);
          terminator = true;
          return;
        }
      });

      if (!terminator) {
        matchDetails?.away_team.match_stats.map((player) => {
          if (player.player_id === playerInfo?.id) {
            setCurrenetPlayer(player);
            setIsPlaying(true);
            return;
          }
        });
      }
    }
  }, [playerInfo, homeTeamRoster, awayTeamRoster]);

  useEffect(() => {
    if (matchDetails) {
      setTimeLimit(MatchLimitISO(matchDetails));
    }
  }, [matchDetails]);

  return (
    <div className='match-container'>
      <div className='match-video-wrapper' style={{ backgroundColor: 'black' }}>
        <div className='match-video'>
          <YoutubeEmbed embedId={matchDetails ? matchDetails.match_videos : 'PlLX5dK-AzQ'} />
        </div>
      </div>
      {currentPlayer && !isStarted && (
        <>
          <div className='notice-message'>
            Please note that the match roster registration will be closed at{' '}
            {timeLimit.replace('T', ' ').slice(0, 16)}
            .<br />
            If you have any enquiry, please contact us through the team captain.
          </div>
          <MatchSignUp player={currentPlayer} matchId={id} status={isPlaying} />
        </>
      )}
      <div className='match-content-wrapper'>
        <GameResult matchDetails={matchDetails} />

        {/* Show MatchRoster always - before match starts, during match, and after match */}
        <MatchRoster matchId={id} isStarted={isStarted} />

        {/* Show tabs only when match is finished */}
        {matchDetails?.home_score !== '-' && matchDetails?.away_score !== '-' ? (
          <TabComponent
            tabs={[
              {
                label: 'Overview',
                content: <MatchOverviewTab matchDetails={matchDetails} />,
              },
              {
                label: 'Play by Play',
                content: <PlayByPlayTab matchDetails={matchDetails} />,
              },
              {
                label: 'Shooting Chart',
                content: <ShootingChartTab matchDetails={matchDetails} />,
              },
              {
                label: 'Game Stats',
                content: <GameStatsTab matchDetails={matchDetails} />,
              },
              {
                label: 'Photos',
                content: <PhotosTab matchPhotos={matchPhotos} />,
              },
            ]}
            defaultTab={0}
          />
        ) : (
          <>
            {/* Show original layout for upcoming matches */}
            <div className='match-photos'>
              <MatchPhotos matchPhotos={matchPhotos} />
            </div>
            {matchDetails?.mvp && <MatchDetailBox matchDetails={matchDetails} />}
            {matchDetails && <HeadToHeadTab matchDetails={matchDetails} />}
            <div className='rosters'>
              <TeamRoster roster={homeTeamRoster} />
              <TeamRoster roster={awayTeamRoster} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Match;
